import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Plus, ArrowClockwise, Trophy, CheckCircle, XCircle, Eye,
  X, Clock, CurrencyDollar, Trash, PencilSimple, Star,
  CaretLeft, Warning, Sparkle,
} from "@phosphor-icons/react";
import apiClient from "../../lib/api";
import AdminSection from "../../components/admin/AdminSection";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const STATUS_STYLE = {
  active:    { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", label: "Activo" },
  draft:     { cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",           label: "Borrador" },
  closed:    { cls: "bg-zinc-500/10 text-zinc-500 border-zinc-500/20",           label: "Cerrado" },
  cancelled: { cls: "bg-red-500/10 text-red-400 border-red-500/20",             label: "Cancelado" },
};

const RESULT_STYLE = {
  won:  { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", label: "Ganado" },
  lost: { cls: "bg-red-500/10 text-red-400 border-red-500/20",             label: "Perdido" },
  void: { cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",          label: "Anulado" },
};

const PURCHASE_STATUS = {
  pending:  { cls: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20", label: "Pendiente" },
  approved: { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", label: "Aprobado" },
  rejected: { cls: "bg-red-500/10 text-red-400 border-red-500/20",           label: "Rechazado" },
};

function fmt(iso) {
  if (!iso) return "—";
  try { return format(new Date(iso), "dd MMM yyyy", { locale: es }); }
  catch { return iso; }
}

/* ─── Result Modal ─────────────────────────────────────────────── */
function ResultModal({ parlay, onClose, onSave }) {
  const [result, setResult] = useState(parlay.result || "");
  const [loading, setLoading] = useState(false);

  const save = async () => {
    if (!result) return;
    setLoading(true);
    await onSave(parlay.id, result);
    setLoading(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="surface rounded-2xl border border-white/[0.1] w-full max-w-sm p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-black text-lg">Registrar Resultado</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/[0.06] text-zinc-400">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-sm text-zinc-400">{parlay.title}</p>
        <div className="grid grid-cols-3 gap-2">
          {["won", "lost", "void"].map(r => (
            <button key={r} onClick={() => setResult(r)}
              className={`py-3 rounded-xl text-sm font-bold border transition-all ${
                result === r ? (r === "won" ? "bg-emerald-500 text-black border-emerald-500" : r === "lost" ? "bg-red-500 text-white border-red-500" : "bg-zinc-600 text-white border-zinc-600") : "bg-white/[0.03] border-white/[0.08] text-zinc-400 hover:text-white"
              }`}>
              {r === "won" ? "Ganado" : r === "lost" ? "Perdido" : "Anulado"}
            </button>
          ))}
        </div>
        <button onClick={save} disabled={!result || loading}
          className="btn-primary w-full" data-testid="parlay-result-save">
          {loading ? "Guardando..." : "Guardar resultado"}
        </button>
      </div>
    </div>
  );
}

/* ─── Purchase Review Modal ────────────────────────────────────── */
function PurchaseModal({ purchase, onClose, onReview }) {
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const review = async (action) => {
    setLoading(true);
    await onReview(purchase.id, action, note);
    setLoading(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="surface rounded-2xl border border-white/[0.1] w-full max-w-lg p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h2 className="font-black text-lg">Revisar Compra de Parlay</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/[0.06] text-zinc-400">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-zinc-500">Parlay:</span><span className="font-semibold">{purchase.parlay_title}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Usuario:</span><span className="font-semibold">{purchase.user_name} · {purchase.user_email}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Método:</span><span className="font-bold text-yellow-400">{purchase.payment_method?.replace("_", " ").toUpperCase()}</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Monto:</span><span className="font-bold text-emerald-400">${purchase.amount} USD</span></div>
          <div className="flex justify-between"><span className="text-zinc-500">Fecha:</span><span>{fmt(purchase.created_at)}</span></div>
        </div>
        {purchase.payment_proof_image && (
          <div className="rounded-xl overflow-hidden border border-white/[0.08]" style={{ maxHeight: 240 }}>
            <img src={purchase.payment_proof_image} alt="Comprobante" className="w-full object-contain" />
          </div>
        )}
        <textarea
          value={note} onChange={e => setNote(e.target.value)}
          placeholder="Nota interna (opcional)..."
          className="input-field resize-none text-sm" rows={2}
        />
        <div className="flex gap-3">
          <button onClick={() => review("approve")} disabled={loading}
            className="flex-1 btn-primary text-sm" data-testid="parlay-purchase-approve">
            <CheckCircle className="w-4 h-4" /> Aprobar
          </button>
          <button onClick={() => review("reject")} disabled={loading}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors" data-testid="parlay-purchase-reject">
            <XCircle className="w-4 h-4" /> Rechazar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ───────────────────────────────────────────── */
export default function AdminParlaysPage() {
  const [parlays, setParlays] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("parlays");
  const [resultModal, setResultModal] = useState(null);
  const [purchaseModal, setPurchaseModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, pu] = await Promise.all([
        apiClient.get("/admin/parlays"),
        apiClient.get("/admin/parlay-purchases"),
      ]);
      setParlays(p.data || []);
      setPurchases(pu.data || []);
    } catch (e) {
      toast.error("Error cargando datos");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteParlayHandler = async (id) => {
    if (!window.confirm("¿Eliminar este parlay permanentemente?")) return;
    try {
      await apiClient.delete(`/admin/parlays/${id}`);
      setParlays(prev => prev.filter(p => p.id !== id));
      toast.success("Parlay eliminado");
    } catch { toast.error("Error al eliminar"); }
  };

  const saveResult = async (id, result) => {
    try {
      await apiClient.patch(`/admin/parlays/${id}/result`, { result });
      await load();
      toast.success("Resultado registrado");
    } catch { toast.error("Error al guardar resultado"); }
  };

  const reviewPurchase = async (id, action, note) => {
    try {
      await apiClient.post(`/admin/parlay-purchases/${id}/review`, { action, admin_note: note });
      await load();
      toast.success(action === "approve" ? "Compra aprobada" : "Compra rechazada");
    } catch { toast.error("Error al procesar"); }
  };

  const pendingCount = purchases.filter(p => p.status === "pending").length;

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-8 pb-16">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
        <div>
          <Link to="/admin" className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300 mb-3">
            <CaretLeft className="w-3.5 h-3.5" /> Panel Admin
          </Link>
          <div className="text-[11px] text-yellow-400 tracking-[0.25em] uppercase font-bold mb-1">Gestión</div>
          <h1 className="text-3xl font-black tracking-tighter">Parlays Premium</h1>
          <p className="text-zinc-500 text-sm mt-1">Crea y gestiona parlays de pago para tus usuarios</p>
        </div>
        <Link to="/admin/parlays/nuevo" className="btn-primary self-start" data-testid="admin-parlay-create-btn">
          <Plus className="w-4 h-4" /> Crear Parlay
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-obsidian-800 border border-white/[0.06] rounded-md mb-6 w-fit">
        <button onClick={() => setTab("parlays")}
          className={`px-4 py-1.5 text-xs font-bold rounded transition-colors ${tab === "parlays" ? "bg-yellow-400 text-black" : "text-zinc-400 hover:text-white"}`}
          data-testid="parlays-tab-list">
          Parlays ({parlays.length})
        </button>
        <button onClick={() => setTab("purchases")}
          className={`px-4 py-1.5 text-xs font-bold rounded transition-colors flex items-center gap-1.5 ${tab === "purchases" ? "bg-yellow-400 text-black" : "text-zinc-400 hover:text-white"}`}
          data-testid="parlays-tab-purchases">
          Compras
          {pendingCount > 0 && (
            <span className="bg-red-500 text-white text-[10px] font-black rounded-full w-4 h-4 flex items-center justify-center">{pendingCount}</span>
          )}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-20 text-zinc-500">Cargando...</div>
      ) : tab === "parlays" ? (
        /* ── Parlays list ── */
        <AdminSection icon={Trophy} title="Parlays" subtitle="Tus parlays premium publicados" color="emerald">
        <div className="space-y-4" data-testid="admin-parlays-list">
          {parlays.length === 0 ? (
            <div className="text-center py-20 text-zinc-600">
              <Trophy className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No hay parlays aún. ¡Crea el primero!</p>
            </div>
          ) : parlays.map(parlay => {
            const st = STATUS_STYLE[parlay.status] || STATUS_STYLE.active;
            const re = parlay.result ? RESULT_STYLE[parlay.result] : null;
            return (
              <div key={parlay.id} className="surface rounded-xl p-5 border border-white/[0.06]" data-testid={`parlay-row-${parlay.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                      {re && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${re.cls}`}>{re.label}</span>}
                      <span className="text-[10px] text-yellow-400 font-bold">Cuota {parlay.total_odds?.toFixed(2)}x</span>
                      <span className="text-[10px] text-emerald-400 font-bold">${parlay.price} USD</span>
                      <span className="text-[10px] text-zinc-500">{parlay.current_sales}/{parlay.max_sales || "∞"} ventas</span>
                    </div>
                    <h3 className="font-black text-lg leading-tight mb-1">{parlay.title}</h3>
                    <p className="text-xs text-zinc-500 line-clamp-2">{parlay.admin_notes}</p>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {(parlay.picks || []).map((pick, i) => (
                        <span key={i} className="text-[10px] bg-white/[0.04] border border-white/[0.06] rounded px-2 py-0.5 text-zinc-400">
                          {pick.home_team} vs {pick.away_team} · {pick.odds?.toFixed(2)}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {parlay.status === "active" && !parlay.result && (
                      <button onClick={() => setResultModal(parlay)}
                        className="btn-secondary text-xs" data-testid={`parlay-set-result-${parlay.id}`}>
                        <Trophy className="w-3.5 h-3.5" /> Resultado
                      </button>
                    )}
                    <Link to={`/admin/parlays/editar/${parlay.id}`}
                      className="p-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-zinc-400 hover:text-white transition-colors"
                      data-testid={`parlay-edit-${parlay.id}`}>
                      <PencilSimple className="w-4 h-4" />
                    </Link>
                    <button onClick={() => deleteParlayHandler(parlay.id)}
                      className="p-2 rounded-lg bg-white/[0.04] hover:bg-red-500/10 text-zinc-400 hover:text-red-400 transition-colors"
                      data-testid={`parlay-delete-${parlay.id}`}>
                      <Trash className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        </AdminSection>
      ) : (
        /* ── Purchases list ── */
        <AdminSection icon={CurrencyDollar} title="Compras" subtitle="Pagos de parlays por revisar y aprobar" color="amber">
        <div className="space-y-3" data-testid="admin-parlay-purchases-list">
          {purchases.length === 0 ? (
            <div className="text-center py-20 text-zinc-600">
              <CurrencyDollar className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No hay compras aún.</p>
            </div>
          ) : purchases.map(purchase => {
            const st = PURCHASE_STATUS[purchase.status] || PURCHASE_STATUS.pending;
            return (
              <div key={purchase.id} className="surface rounded-xl p-4 border border-white/[0.06] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={`purchase-row-${purchase.id}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                    <span className="text-xs font-bold text-emerald-400">${purchase.amount} USD</span>
                    <span className="text-xs text-zinc-500">{purchase.payment_method?.replace("_", " ")}</span>
                  </div>
                  <p className="font-semibold text-sm truncate">{purchase.parlay_title}</p>
                  <p className="text-xs text-zinc-500">{purchase.user_name} · {purchase.user_email} · {fmt(purchase.created_at)}</p>
                </div>
                {purchase.status === "pending" && (
                  <button onClick={() => setPurchaseModal(purchase)}
                    className="btn-secondary text-xs flex-shrink-0" data-testid={`purchase-review-${purchase.id}`}>
                    <Eye className="w-3.5 h-3.5" /> Revisar
                  </button>
                )}
              </div>
            );
          })}
        </div>
        </AdminSection>
      )}

      {resultModal && <ResultModal parlay={resultModal} onClose={() => setResultModal(null)} onSave={saveResult} />}
      {purchaseModal && <PurchaseModal purchase={purchaseModal} onClose={() => setPurchaseModal(null)} onReview={reviewPurchase} />}
    </div>
  );
}
